import React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Box } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { IconArrowRight } from '@lightspeed/flame/Icon/ArrowRight';
import { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';

import { CustomerInfoDiff, DiffRenderer } from './DiffRenderer';
import {
  BoxWithCustomScroll,
  CustomerInfoDiv,
  FixedHeightButton,
  InteractiveFlex,
  MinWidthModal,
} from './ExistingCustomerSelection.style';
import { CfdCustomerDataInputs } from './types';
import { buildAddress } from './utils';

export const CustomerInfo = ({
  customerID,
  firstName,
  lastName,
  Contact: {
    Phones,
    Emails,
    Addresses: { ContactAddress },
  },
  onSelection,
  loadingCustomer,
  customerInput,
  reference,
}: Customer &
  CustomerSelectionProp & {
    loadingCustomer: string | undefined;
    reference?: React.MutableRefObject<{
      [key: string]: HTMLDivElement;
    }>;
  }) => {
  const hasCustomerInfoConfirmation = window.lsCloud.Modules.hasModule(
    'register.cfd.info_confirmation'
  );
  const getContactDiffElements = (field: any, searchKey: 'number' | 'address') => {
    if (!field) return [];

    const fieldCopy = Array.isArray(field) ? [...field] : [field];

    let lookup: any;
    if (searchKey === 'address') {
      lookup = customerInput?.emails?.primary;
    } else {
      lookup = customerInput?.phones?.mobile;
    }

    return fieldCopy
      .map((item) => item?.[searchKey])
      .filter(Boolean)
      .map((entry, idx, originalArray) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={`${customerID}-diff-${idx}`}>
            <DiffRenderer dataInDb={entry} newData={lookup} showMatch />
            {idx + 1 === originalArray.length ? '' : ', '}
          </React.Fragment>
        );
      });
  };

  const addressString = buildAddress(ContactAddress);
  const phone = typeof Phones === 'string' ? undefined : Phones.ContactPhone;
  const phoneDiffElements = getContactDiffElements(phone, 'number');
  const email = typeof Emails === 'string' ? undefined : Emails.ContactEmail;
  const emailDiffElements = getContactDiffElements(email, 'address');

  return (
    <InteractiveFlex
      justifyContent="space-between"
      alignItems="center"
      ref={(divRef) => {
        // Keep track of all references to scroll last selected customer info into view
        if (reference) {
          reference.current[customerID] = divRef;
        }
      }}
    >
      <Box
        data-test={`customer-info-${customerID}`}
        padding={hasCustomerInfoConfirmation ? '.75rem 0' : 3}
        style={{ maxWidth: '320px' }}
      >
        {firstName && lastName && (
          <CustomerInfoDiv
            style={{ fontWeight: hasCustomerInfoConfirmation ? undefined : 'bold' }}
            data-test="customer-name"
          >
            {hasCustomerInfoConfirmation ? (
              <var>{`${firstName} ${lastName}`}</var>
            ) : (
              <a
                rel="noopener noreferrer"
                href={`/?name=customer.views.customer&form_name=view&id=${customerID}&tab=details`}
                target="_blank"
              >
                <var>{`${firstName} ${lastName}`}</var>
              </a>
            )}
          </CustomerInfoDiv>
        )}
        {phoneDiffElements.length > 0 && (
          <CustomerInfoDiv data-test="customer-phones">
            {hasCustomerInfoConfirmation ? null : 'Phone(s):'}
            {phoneDiffElements}
          </CustomerInfoDiv>
        )}
        {emailDiffElements.length > 0 && (
          <CustomerInfoDiv data-test="customer-email">
            {hasCustomerInfoConfirmation ? null : 'Email(s):'}
            {emailDiffElements}
          </CustomerInfoDiv>
        )}
        {addressString && hasCustomerInfoConfirmation && (
          <CustomerInfoDiv data-test="customer-address">{addressString}</CustomerInfoDiv>
        )}
      </Box>
      <Box m={2}>
        <Button
          id={`select-${customerID}`}
          disabled={loadingCustomer !== undefined}
          loading={loadingCustomer === customerID}
          variant={hasCustomerInfoConfirmation ? undefined : 'secondary'}
          onClick={() => onSelection(customerID)}
        >
          Select
        </Button>
      </Box>
    </InteractiveFlex>
  );
};

export default function ExistingCustomerSelection({
  customers,
  onSelection,
  isOpen,
  modalStateToggle,
  customerInput,
}: {
  customers: Customer[];
  isOpen: boolean;
  modalStateToggle: (state: boolean) => void;
} & CustomerSelectionProp) {
  const hasCustomerInfoConfirmation = window.lsCloud.Modules.hasModule(
    'register.cfd.info_confirmation'
  );
  const [currentLoadingCustomer, setCurrentLoadingCustomer] = React.useState<string | undefined>(
    undefined
  );
  const [selectedCustomer, setSelectedCustomer] = React.useState<string | undefined>(undefined);
  const [lastCustomerViewed, setLastCustomerViewed] = React.useState<string | undefined>(undefined);
  const allCustomerInfoRefs = React.useRef<{ [key: string]: HTMLDivElement }>({});

  React.useEffect(() => {
    if (allCustomerInfoRefs && !selectedCustomer && lastCustomerViewed) {
      allCustomerInfoRefs.current[lastCustomerViewed].scrollIntoView({
        inline: 'start',
      });
    }
  }, [lastCustomerViewed, selectedCustomer]);

  const onCustomerSelection = async (customerID?: string) => {
    if (hasCustomerInfoConfirmation) {
      setSelectedCustomer(customerID);
      setLastCustomerViewed(customerID);
    } else {
      setCurrentLoadingCustomer(customerID);
      await onSelection(customerID);
      modalStateToggle(false);
    }
  };

  const handleCreateNewCustomerClick = async () => {
    setCurrentLoadingCustomer('new_customer');
    await onSelection();
    modalStateToggle(false);
  };

  const handleKeepProfileSameClick = async () => {
    setCurrentLoadingCustomer('keep_profile');
    await onSelection(selectedCustomer, true);
    modalStateToggle(false);
  };

  const handleUpdateProfileClick = async () => {
    setCurrentLoadingCustomer(selectedCustomer);
    await onSelection(selectedCustomer);
    modalStateToggle(false);
  };

  const selectedCustomerData = customers.find((entry) => entry.customerID === selectedCustomer);

  const modalTitle = selectedCustomer ? 'Customer profile' : 'Customer profile matches';
  const modalBody = selectedCustomer ? (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box style={{ width: '45%' }}>
          {selectedCustomerData && (
            <CustomerInfoDiff
              title="Existing customer profile"
              dataInDb={customerDataToDiffData(selectedCustomerData)}
            />
          )}
        </Box>
        <Box mt={3}>
          <IconArrowRight color="rgba(35, 36, 41, 0.7)" />
        </Box>
        <Box style={{ width: '45%' }}>
          {selectedCustomerData && customerInput && (
            <CustomerInfoDiff
              title="New customer profile"
              dataInDb={customerDataToDiffData(selectedCustomerData)}
              formData={formDataToDiffDataFormat(customerInput)}
            />
          )}
        </Box>
      </Box>
      <Text mt={2}>
        Update the existing customer profile with the information that the customer has entered, or
        keep the existing profile as is.
      </Text>
    </>
  ) : (
    <>
      <Box mb={3}>
        {customerInput && (
          <CustomerInfoDiff
            title="New customer profile"
            formData={formDataToDiffDataFormat(customerInput)}
          />
        )}
      </Box>
      <Box mt={3} mb={3}>
        <Text fontWeight={700} color="#020203" mb="6px">
          Existing customer profiles
        </Text>
        <Text>
          We found matches based on email and phone number. Select a customer to avoid creating a
          duplicate or create a new customer if needed.
        </Text>
      </Box>
      <BoxWithCustomScroll style={{ maxHeight: '40vh', overflow: 'auto' }}>
        {customers.map(({ customerID, ...rest }) => (
          <CustomerInfo
            customerInput={customerInput}
            reference={allCustomerInfoRefs}
            key={customerID}
            {...rest}
            customerID={customerID}
            onSelection={onCustomerSelection}
            loadingCustomer={currentLoadingCustomer}
          />
        ))}
      </BoxWithCustomScroll>
    </>
  );

  const modalFooter = selectedCustomer ? (
    <Box justifyContent="space-between" display="flex">
      <Box>
        <FixedHeightButton
          disabled={!!currentLoadingCustomer}
          key="back"
          onClick={() => setSelectedCustomer(undefined)}
        >
          Back
        </FixedHeightButton>
      </Box>
      <Box>
        <FixedHeightButton
          data-test="keep-profile-button"
          loading={currentLoadingCustomer === 'keep_profile'}
          disabled={!!currentLoadingCustomer}
          onClick={handleKeepProfileSameClick}
          style={{ marginRight: '10px' }}
        >
          Keep profile as is
        </FixedHeightButton>
        <FixedHeightButton
          data-test="update-profile-button"
          loading={currentLoadingCustomer === selectedCustomer}
          disabled={!!currentLoadingCustomer}
          fill
          variant="secondary"
          onClick={handleUpdateProfileClick}
        >
          Update profile
        </FixedHeightButton>
      </Box>
    </Box>
  ) : (
    <Box display="flex" flexDirection="row-reverse">
      <FixedHeightButton
        data-test="new-customer-button"
        key="new-customer"
        mr={customers.length > 4 ? 3 : 2}
        disabled={currentLoadingCustomer !== undefined}
        loading={currentLoadingCustomer === 'new_customer'}
        onClick={handleCreateNewCustomerClick}
      >
        Create new customer
      </FixedHeightButton>
    </Box>
  );

  return (
    <>
      <MinWidthModal isOpen={isOpen}>
        {hasCustomerInfoConfirmation ? (
          <Box fontSize="14px" color="#5C626A" lineHeight="18px">
            <ModalHeader data-test="modal-header" showCloseButton={false}>
              {modalTitle}
            </ModalHeader>
            <ModalBody data-test="existing-customer-list-modal">{modalBody}</ModalBody>
            <ModalFooter style={{ backgroundColor: selectedCustomer ? undefined : '#FFF' }}>
              {modalFooter}
            </ModalFooter>
          </Box>
        ) : (
          // Remove this when the feature flag is removed
          <>
            <ModalBody data-test="existing-customer-list-modal">
              <BoxWithCustomScroll mb={3} style={{ maxHeight: '50vh', overflow: 'auto' }}>
                {customers.map(({ customerID, ...rest }) => (
                  <CustomerInfo
                    key={customerID}
                    {...rest}
                    customerID={customerID}
                    onSelection={onCustomerSelection}
                    loadingCustomer={currentLoadingCustomer}
                  />
                ))}
              </BoxWithCustomScroll>
              <Divider>Or</Divider>
              <Box padding={3}>
                <FixedHeightButton
                  disabled={currentLoadingCustomer !== undefined}
                  loading={currentLoadingCustomer === 'new_customer'}
                  onClick={handleCreateNewCustomerClick}
                >
                  Create new customer
                </FixedHeightButton>
              </Box>
            </ModalBody>
          </>
        )}
      </MinWidthModal>
    </>
  );
}

const customerDataToDiffData = (customerData: Customer) => {
  const {
    firstName,
    lastName,
    Contact: { Emails, Phones },
  } = customerData;
  const name = firstName && lastName ? `${firstName} ${lastName}` : '';

  let mobilePhone: string | undefined;
  let primaryEmail: string | undefined;
  if (typeof Phones === 'string') {
    mobilePhone = undefined;
  } else if (Array.isArray(Phones.ContactPhone)) {
    const mobilePhoneMatch = Phones.ContactPhone?.find((entry) => entry.useType === 'Mobile');
    mobilePhone = mobilePhoneMatch ? mobilePhoneMatch.number : mobilePhoneMatch;
  } else {
    mobilePhone = Phones.ContactPhone.useType === 'Mobile' ? Phones.ContactPhone.number : undefined;
  }

  if (typeof Emails === 'string') {
    primaryEmail = undefined;
  } else if (Array.isArray(Emails.ContactEmail)) {
    primaryEmail = Emails.ContactEmail?.find((entry) => entry.useType === 'Primary')?.address;
  } else {
    primaryEmail = Emails.ContactEmail.useType === 'Primary' ? Emails.ContactEmail.address : '';
  }

  return { name, primaryEmail, mobilePhone };
};

const formDataToDiffDataFormat = (customerInput: CfdCustomerDataInputs) => {
  const { firstName, lastName, emails, phones } = customerInput;
  const fullName = `${firstName} ${lastName}`;
  const mobilePhone = phones?.mobile;
  const primaryEmail = emails?.primary;

  return { name: fullName.trim(), mobilePhone, primaryEmail };
};
